import { Button } from "@tql/ui-components-react/tql/buttons";
import { file_06, xLine } from "@tql/ui-components-react/untitled/icons";
import { useCallback, useMemo } from "react";

import { DocumentViewer } from "~/shared/components/document-viewer/document-viewer.tsx";
import { useDocumentViewer } from "~/shared/components/document-viewer-flyout/hooks/use-document-viewer.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type { Load } from "~/shared/schemas/loads-schema.ts";

import { useLoadDocumentGeneration } from "../../hooks/use-load-document-generation.tsx";
import { TitleCardHolder } from "../title-card-holder/title-card-holder.tsx";
import type { AdditionalDetailsInfo } from "./drawer-additional-details/drawer-additional-details.tsx";
import { DrawerAdditionalDetails } from "./drawer-additional-details/drawer-additional-details.tsx";
import type { LtlItem } from "./drawer-item-details/drawer-item-details.tsx";
import { DrawerItemDetails } from "./drawer-item-details/drawer-item-details.tsx";
import { DrawerShipmentDetails } from "./drawer-shipment-details/drawer-shipment-details.tsx";
import { DrawerTracking } from "./drawer-tracking/drawer-tracking.tsx";
import { useShipmentFlyout } from "./flyout-details.ts";
import styles from "./load-details-drawer.module.css";

type LoadDetailsDrawerProps = {
	readonly isOpen: boolean;
	readonly onClose: () => void;
	readonly load: Load | null;
};

export type FlyoutDetailsData = {
	poNumber: number;
	ltlItems: LtlItem[];
	additionalDetails: AdditionalDetailsInfo;
};

const RateTypeIds = {
	LTL: 5,
	PartialsDeptLTL: 24,
};

export function LoadDetailsDrawer({
	isOpen,
	onClose,
	load,
}: Readonly<LoadDetailsDrawerProps>) {
	const {
		data: details,
		isLoading,
		isFetching,
	} = useShipmentFlyout(load?.poNumber, isOpen);

	const { viewerState, openViewer, closeViewer, selectDocument } =
		useDocumentViewer();

	const { isInternal } = useDeploymentType();
	const {
		headerAction: generateDocumentsHeaderAction,
		modal: generateDocumentsModal,
	} = useLoadDocumentGeneration({ load, isInternal });

	const handleDocumentClick = useCallback(() => {
		openViewer();
	}, [openViewer]);

	const drawerCards = useMemo(() => {
		const cards = [
			{
				id: "tracking",
				title: "Tracking",
				content: <DrawerTracking load={load} />,
				initiallyExpanded: true,
			},
			{
				id: "shipment-details",
				title: "Shipment Details",
				content: <DrawerShipmentDetails load={load} />,
				initiallyExpanded: true,
			},
		];
		// PartialsDeptLTL shipments will sometimes have ltlItems.
		// If built through Trax they will be present.
		// If built manually through Load Manager then they will not be present.
		// We will wait on the API results to determine if ltlItems are available.
		if (
			load?.rateTypeId == RateTypeIds.LTL ||
			(load?.rateTypeId == RateTypeIds.PartialsDeptLTL &&
				details?.ltlItems &&
				details?.ltlItems.length > 0)
		)
			cards.push({
				id: "item-details",
				title: "Item Details",
				content: (
					<DrawerItemDetails
						isLoading={isLoading || isFetching}
						items={details?.ltlItems ?? []}
					/>
				),
				initiallyExpanded: true,
			});
		cards.push({
			id: "additional-details",
			title: "Additional Details",
			content: (
				<DrawerAdditionalDetails
					additionalDetails={details?.additionalDetails}
					isLoading={isLoading || isFetching}
					load={load}
				/>
			),
			initiallyExpanded: true,
		});
		return cards;
	}, [
		load,
		details?.additionalDetails,
		details?.ltlItems,
		isLoading,
		isFetching,
	]);

	if (!isOpen) {
		return null;
	}

	return (
		<div>
			<div
				aria-hidden={true}
				className={styles.backdrop}
				data-testid={"drawer-backdrop"}
				onClick={onClose}
			/>
			<aside aria-label={"Load details drawer"} className={styles.panel}>
				<div className={styles.header}>
					<h2 className={styles.title}>TQL PO: {load?.poNumber ?? "-"}</h2>
					<div className={styles.headerActions}>
						<Button
							aria-label={"View shipment documents"}
							data-testid={"drawer-document-trigger"}
							onClick={handleDocumentClick}
							svgIcon={file_06}
							variant={"tertiary"}
						/>
						<Button
							aria-label={"Close load details drawer"}
							onClick={onClose}
							svgIcon={xLine}
							variant={"tertiary"}
						/>
					</div>
				</div>
				<div className={styles.body}>
					<TitleCardHolder cards={drawerCards} />
				</div>
			</aside>
			<DocumentViewer
				extraHeaderActions={generateDocumentsHeaderAction}
				onClose={closeViewer}
				onSelectDocument={selectDocument}
				poNumber={load?.poNumber ?? undefined}
				viewerState={viewerState}
			/>
			{generateDocumentsModal}
		</div>
	);
}
